import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
	LWApiPublicAccountUserStateDataUpdateParamsRawI,
	LWApiPublicAccountUserStateDataUpdateResultRawI,
	LWApiPublicAccountUserStateDataListForKeyResultRawI,
} from '@liquidweb-dev/club-sauce/public/account/user/stateData/index.raw';
import type { ApiErrorI } from '@liquidweb-dev/club-sauce/types/apiError';
import api from 'modules/queries/api';
import useSnackbarError from 'utility/hooks/useSnackbarError';
import { ACCOUNT_USER_STATEDATA_LISTFORKEY_QUERY_KEY } from './useListForKey';

export const useStatedataUpdate = () => {
	const queryClient = useQueryClient();
	const snackbarError = useSnackbarError();

	return useMutation<
		LWApiPublicAccountUserStateDataUpdateResultRawI,
		ApiErrorI,
		LWApiPublicAccountUserStateDataUpdateParamsRawI
	>({
		mutationFn: (params) => api.account.user.stateData.apiRaw.update(params),

		onMutate: async (params) => {
			await queryClient.cancelQueries({
				queryKey: [
					ACCOUNT_USER_STATEDATA_LISTFORKEY_QUERY_KEY,
					{ key: params.key },
				],
			});

			queryClient.setQueryData<LWApiPublicAccountUserStateDataListForKeyResultRawI>(
				[ACCOUNT_USER_STATEDATA_LISTFORKEY_QUERY_KEY, { key: params.key }],
				(current) => {
					if (!current) {
						return current;
					}

					const data = JSON.parse(params.data);
					const updatedData = current.data.filter((d) => d.uuid !== data.uuid);

					return {
						...current,
						data: [data, ...updatedData],
					};
				},
			);
		},

		onSettled: (_result, _error, params) => {
			queryClient.invalidateQueries({
				queryKey: [
					ACCOUNT_USER_STATEDATA_LISTFORKEY_QUERY_KEY,
					{ key: params.key },
				],
			});
		},

		onError: snackbarError,
	});
};

export const useUpdateFavorite = () => {
	const queryClient = useQueryClient();

	return useMutation<
		LWApiPublicAccountUserStateDataUpdateResultRawI,
		ApiErrorI,
		{ uuid: string; path: string; displayName: string }
	>({
		mutationFn: ({ uuid, path, displayName }) =>
			api.account.user.stateData.apiRaw.update({
				key: 'favorites',
				data: JSON.stringify({ uuid, path, displayName }),
			}),

		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [
					ACCOUNT_USER_STATEDATA_LISTFORKEY_QUERY_KEY,
					{ key: 'favorites' },
				],
			});
		},
	});
};

export default useStatedataUpdate;
